import { computed } from "vue"
import { defineStore } from "pinia"

import useLegacyStore from "@Composables/useLegacyStore"
import OrdersApi, { CheckoutResponse } from "../Api/OrdersApi"
import { Order } from "../Data/Models/OrderModel"

export const XOrdersStore = defineStore("x-orders", () => {
  //
  // Setup
  const api = OrdersApi()

  //
  // Computed
  const orders = api.orders
  const loading = api.loading
  const errors = api.errors
  const count = computed(() => api.orders.value.length)
  const isEmpty = computed(() => api.orders.value.length === 0)
  const getOrderById = api.getOrderById

  //
  // Methods
  async function loadOrdersForCurrentUser(): Promise<void> {
    const legacyStore = useLegacyStore()
    if (!legacyStore?.getters?.isUserLoggedIn) {
      return
    }

    await api.fetchOrders()
  }

  async function loadOrdersForOrganization(orgSlug: string): Promise<void> {
    await api.fetchOrdersForOrganization(orgSlug)
  }

  async function fetchOrder(
    orderId: number,
    options?: { disableErrorInterceptors?: boolean },
  ): Promise<Order | null> {
    return api.fetchOrder(orderId, options)
  }

  async function fetchOrderForOrganization(
    orgSlug: string,
    orderId: number,
    options?: { disableErrorInterceptors?: boolean },
  ): Promise<Order | null> {
    return api.fetchOrderForOrganization(orgSlug, orderId, options)
  }

  async function fetchOrderBySession(
    session: string,
    options?: { disableErrorInterceptors?: boolean },
  ): Promise<Order | null> {
    return api.fetchOrderBySession(session, options)
  }

  async function fetchOrderBySessionForOrganization(
    orgSlug: string,
    session: string,
    options?: { disableErrorInterceptors?: boolean },
  ): Promise<Order | null> {
    return api.fetchOrderBySessionForOrganization(orgSlug, session, options)
  }

  async function checkout(instance: string): Promise<CheckoutResponse | null> {
    return api.checkout(instance)
  }

  async function checkoutForOrganization(orgSlug: string, instance: string): Promise<CheckoutResponse | null> {
    return api.checkoutForOrganization(orgSlug, instance)
  }

  async function retryCheckout(orderId: number): Promise<CheckoutResponse | null> {
    return api.retryCheckout(orderId)
  }

  function getOrderPdfUrl(orderId: number): string {
    return api.getOrderPdfUrl(orderId)
  }

  function getOrderPdfUrlForOrganization(orgSlug: string, orderId: number): string {
    return api.getOrderPdfUrlForOrganization(orgSlug, orderId)
  }

  function clearOrders(): void {
    api.clearOrders()
  }

  function clearErrors(): void {
    api.clearErrors()
  }

  return {
    orders,
    count,
    isEmpty,
    loading,
    errors,
    getOrderById,
    loadOrdersForCurrentUser,
    loadOrdersForOrganization,
    fetchOrder,
    fetchOrderForOrganization,
    fetchOrderBySession,
    fetchOrderBySessionForOrganization,
    checkout,
    checkoutForOrganization,
    retryCheckout,
    getOrderPdfUrl,
    getOrderPdfUrlForOrganization,
    clearOrders,
    clearErrors,
  }
})
